import React, { useEffect, useState } from "react";
import {
  Table,
  Input,
  DatePicker,
  Button,
  Row,
  Col,
  Typography,
  Space,
  Tag,
  Divider,
  message,
} from "antd";
import { SearchOutlined, FilePdfOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import moment from "moment";
import dayjs from "dayjs";
import { getGajiKaryawan, downloadGajiPdf } from "../../api/transaksi/Gaji";
const { MonthPicker } = DatePicker;

const formatRupiah = (angka) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
  }).format(angka);

const SalaryReport = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [loading, setLoading] = useState(false);

  let isAdmin = false;
  const sessiData = JSON.parse(sessionStorage.getItem("user"));
  if (
    sessiData &&
    Array.isArray(sessiData) &&
    sessiData[0]?.user?.[0]?.rolename
  ) {
    const adminRoles = JSON.parse(process.env.REACT_APP_ROLEADMIN || "[]");
    isAdmin = adminRoles.includes(sessiData[0].user[0].rolename);
  }

  useEffect(() => {
    if (selectedMonth) fetchData();
  }, [selectedMonth]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await getGajiKaryawan(
        sessiData[0].user[0].employeeId,
        selectedMonth
      );
      if (res.status && res.data) {
        let dataArray = [];
        if (Array.isArray(res.data)) {
          dataArray = res.data;
        } else if (data) {
          dataArray = [res.data];
        }

        const transformed = dataArray.map((d) => ({
          key: d.gji_id_gaji,
          id_karyawan: d.gji_id_karyawan,
          nama: d.emp_nama,
          periode: d.gji_periode,
          tanggal_dibayarkan: d.gji_tanggal_bayar,
          status: d.gji_status === "Paid" ? "Lunas" : "Belum Dibayar",
          gaji_pokok: d.gji_gapok || 0,
          gaji_diterima: d.gji_total_diterima || 0,
          gaji_kotor: d.gji_total_gaji || 0,
          tunjangan: (d.tunjangan_jabatan || []).map((t) => ({
            keterangan: t.nama_tunjangan,
            tanggal: d.gji_tanggal_bayar,
            nominal: t.jumlah,
          })),
          lembur: (d.lembur || []).map((l) => ({
            keterangan: l.keterangan,
            tanggal: l.tanggal,
            nominal: l.biaya,
          })),
          insentif: (d.insentif || []).map((i) => ({
            keterangan: i.keterangan,
            tanggal: d.gji_tanggal_bayar,
            nominal: i.jumlah,
          })),
          potongan: (d.potongan || []).map((p) => ({
            keterangan: p.keterangan,
            tanggal: d.gji_tanggal_bayar,
            nominal: p.jumlah,
          })),
        }));

        setData(transformed);
        setFilteredData(transformed);
      } else {
        message.error("Gagal mengambil data");
        setData([]);
        setFilteredData([]);
      }
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Terjadi kesalahan saat mengambil data";
      message.error(errorMessage);
      setData([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async (employeeId, periode) => {
    try {
      const res = await downloadGajiPdf(employeeId, periode);
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `slip_gaji_${employeeId}_${periode}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      message.error("Gagal mengunduh file PDF");
    }
  };

  const getTotal = (arr) =>
    arr?.reduce((sum, item) => sum + (item.nominal || 0), 0) || 0;

  const getTotalGaji = (record) =>
    record.gaji_pokok +
    getTotal(record.tunjangan) +
    getTotal(record.lembur) +
    getTotal(record.insentif) -
    getTotal(record.potongan);

  const handleSearch = () => {
    const filtered = data.filter((item) =>
      item.nama.toLowerCase().includes(searchText.toLowerCase())
    );
    setFilteredData(filtered);
  };

  const handleReset = () => {
    setSearchText("");
    setFilteredData(data);
    setSelectedMonth(null);
  };

  const handleMonthChange = (date, dateString) => {
    setSelectedMonth(dateString);
  };

  const handleExport = () => {
    const exportData = filteredData.map((item) => {
      const row = {};
      if (isAdmin) {
        row["Nama Karyawan"] = item.nama || "-";
      }

      row["Total Gaji"] = item.gaji_kotor;
      row["Total Gaji Diterima"] = item.gaji_diterima;
      row["Periode"] = item.periode
        ? dayjs(item.periode).format("YYYY-MM")
        : "-";
      row["Tanggal Dibayarkan"] = item.tanggal_dibayarkan
        ? dayjs(item.gji_tanggal_bayar).format("YYYY-MM-DD")
        : "-";
      row["Status Pembayaran"] = item.status;

      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Laporan Gaji");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const dataBlob = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });
    saveAs(dataBlob, "laporan_gaji.xlsx");
  };

  const renderDetailTable = (data) => (
    <Table
      size="small"
      showHeader={true}
      columns={[
        {
          title: "Keterangan",
          dataIndex: "keterangan",
          key: "keterangan",
          width: 300,
        },
        {
          title: "Nominal",
          dataIndex: "nominal",
          key: "nominal",
          width: 150,
          render: (val) => formatRupiah(val),
        },
      ]}
      dataSource={data}
      pagination={false}
      bordered
      rowKey={(row, idx) => idx}
      style={{ marginBottom: 16 }}
      summary={(pageData) => {
        const total = pageData.reduce(
          (sum, row) => sum + (row.nominal || 0),
          0
        );
        return (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0}>
              <strong>Total</strong>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={1}>
              <strong>{formatRupiah(total)}</strong>
            </Table.Summary.Cell>
          </Table.Summary.Row>
        );
      }}
    />
  );

  const expandedRowRender = (record) => (
    <div style={{ padding: 8 }}>
      <p>
        <strong>Gaji Pokok:</strong> {formatRupiah(record.gaji_pokok)}
      </p>
      <div style={{ marginTop: 16 }}>
        {record.tunjangan?.length > 0 && (
          <>
            <Divider strong>Tunjangan</Divider>
            {renderDetailTable(record.tunjangan)}
          </>
        )}
        {record.lembur?.length > 0 && (
          <>
            <Divider strong>Lembur</Divider>
            {renderDetailTable(record.lembur)}
          </>
        )}
        {record.insentif?.length > 0 && (
          <>
            <Divider strong>Insentif</Divider>
            {renderDetailTable(record.insentif)}
          </>
        )}
        {record.potongan?.length > 0 && (
          <>
            <Divider strong>Potongan</Divider>
            {renderDetailTable(record.potongan)}
          </>
        )}
      </div>
    </div>
  );

  const columns = [
    ...(isAdmin
      ? [
          {
            title: "Nama Karyawan",
            dataIndex: "nama",
            key: "nama",
            filterDropdown: () => (
              <div style={{ padding: 8 }}>
                <Input
                  placeholder="Cari nama"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onPressEnter={handleSearch}
                  style={{ width: 188, marginBottom: 8, display: "block" }}
                />
                <Space>
                  <Button
                    type="primary"
                    icon={<SearchOutlined />}
                    size="small"
                    onClick={handleSearch}
                  >
                    Cari
                  </Button>
                  <Button size="small" onClick={handleReset}>
                    Reset
                  </Button>
                </Space>
              </div>
            ),
          },
        ]
      : []),
    {
      title: "Total Gaji",
      key: "gaji_kotor",
      key: "gaji_kotor",
      render: (_, record) => formatRupiah(record.gaji_kotor),
    },
    {
      title: "Total Gaji Diterima",
      key: "gaji_diterima",
      key: "gaji_diterima",
      render: (_, record) => formatRupiah(record.gaji_diterima),
    },
    {
      title: "Periode",
      dataIndex: "periode",
      key: "periode",
      sorter: (a, b) => a.periode.localeCompare(b.periode),
      render: (periode) =>
        periode && dayjs(periode).isValid()
          ? dayjs(periode).locale("id").format("MMMM YYYY")
          : "-",
    },
    {
      title: "Tanggal Dibayarkan",
      dataIndex: "tanggal_dibayarkan",
      key: "tanggal_dibayarkan",
      render: (val) => moment(val).format("DD MMM YYYY"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: [
        { text: "Lunas", value: "Lunas" },
        { text: "Belum Dibayar", value: "Belum Dibayar" },
      ],
      onFilter: (value, record) => record.status === value,
      render: (status) =>
        status === "Lunas" ? (
          <Tag color="green">Lunas</Tag>
        ) : (
          <Tag color="red">Belum Dibayar</Tag>
        ),
    },
    {
      title: "Download",
      key: "download",
      render: (_, record) =>
        record.status === "Lunas" ? (
          <Button
            type="link"
            icon={<FilePdfOutlined style={{ color: "red" }} />}
            onClick={() =>
              handleDownloadPdf(record.id_karyawan, record.periode)
            }
          >
            Slip Gaji
          </Button>
        ) : null,
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Divider>Laporan Gaji Karyawan</Divider>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col>
          <MonthPicker onChange={handleMonthChange} placeholder="Pilih Bulan" />
        </Col>
        <Col>
          <Button type="primary" onClick={handleExport}>
            Export ke Excel
          </Button>
        </Col>
      </Row>

      <Table
        loading={loading}
        columns={columns}
        dataSource={filteredData}
        expandable={{ expandedRowRender }}
        pagination={{
          pageSizeOptions: ["10", "20", "40", "60"],
          showSizeChanger: true,
          defaultPageSize: 20,
        }}
        rowKey="key"
      />
    </div>
  );
};

export default SalaryReport;
